import React from "react";
import ArchitectureIcon from "../../../shared/components/icons/ArchitectureIcon";
import LocationOnIcon from "../../../shared/components/icons/LocationOnIcon";
import { BusinessEntities, UseCase } from "../lib/types";
import BusinessEntityDottedLine from "./BusinessEntityDottedLine";
import BusinessEntityUseCaseTags from "./BusinessEntityUseCaseTags";

import { Box } from "../../../shared/components/ui/chakra";
import { Divider, Flex, HStack, Image, Show, Stack, Text } from "@chakra-ui/react";

const BusinessEntityListItem = ({
  businessEntity: {
    contractor_info,
    client_info,
    logo_image_url,
    name,
    branch_name,
    industry,
    business_type,
    prefecture,
    introduction,
  },
  showUseCase,
}: {
  businessEntity: BusinessEntities[number];
  showUseCase: UseCase;
}) => {
  let useCase: UseCase;

  switch (true) {
    case showUseCase === "both" &&
      contractor_info != null &&
      client_info == null:
      useCase = "contractor";
      break;
    case showUseCase === "both" &&
      contractor_info == null &&
      client_info != null:
      useCase = "client";
      break;
    default:
      useCase = showUseCase;
  }

  const HeaderForSPSize = () => {
    return (
      <Stack gap={4} display={{ base: "flex", md: "none" }}>
        <BusinessEntityUseCaseTags useCase={useCase} />
        <Flex gap={3}>
          <Box w={32} aspectRatio={"3/2"} flexShrink={0}>
            {logo_image_url ? (
              <Image
                src={logo_image_url}
                alt={`${name}のロゴ`}
                aspectRatio={1 / 1}
                h="full"
                mx="auto"
              />
            ) : (
              <Flex
                bgColor={"brand.100"}
                align={"center"}
                justify={"center"}
                borderRadius={"4px"}
                p={2}
                aspectRatio={"3/2"}
              >
                <Text fontSize={"xs"} color={"textGray"}>
                  {name}
                </Text>
              </Flex>
            )}
          </Box>
          <Stack gap={1}>
            <Box>
              <Text
                fontSize="sm"
                pl={0.5}
                display="inline"
                verticalAlign="middle"
              >
                {name}
              </Text>
              {branch_name && (
                <Text
                  as="span"
                  fontSize="sm"
                  color="textGray"
                  ml={0.5}
                  display="inline"
                  verticalAlign="middle"
                >
                  ({branch_name})
                </Text>
              )}
            </Box>
            <Stack gap={0.5}>
              <HStack gap={0.5} align={"flex-start"}>
                <ArchitectureIcon boxSize={4} color={"brand.500"} mt={0.5} />
                <HStack gap={1} flexWrap={"wrap"}>
                  <Text
                    fontSize={"xs"}
                    pt={"1px"}
                    color={"textGray"}
                    mt={"1px"}
                    wordBreak={"keep-all"}
                  >
                    {industry.name}
                  </Text>
                  <Text
                    fontSize={"xs"}
                    pt={"1px"}
                    color={"textGray"}
                    px={1}
                    borderRadius={"2px"}
                    bgColor={"gray.light"}
                  >
                    {business_type}
                  </Text>
                </HStack>
              </HStack>
              <HStack gap={0.5}>
                <LocationOnIcon boxSize={4} color={"brand.500"} />
                <Text fontSize={"xs"} pt={"1px"} color={"textGray"} mt={"1px"}>
                  {prefecture.name}
                </Text>
              </HStack>
            </Stack>
          </Stack>
        </Flex>
      </Stack>
    );
  };

  return (
    <Stack
      bgColor={"white"}
      borderRadius="8px"
      pl={{ base: 4, md: 7 }}
      pr={{ base: 4, md: 8 }}
      pt={{ base: 4, md: 7 }}
      pb={{ base: 5, md: 7 }}
      flexDir={{ base: "column", md: "row" }}
      gap={{ base: 3, md: 9 }}
      _hover={{
        outline: "4px solid",
        outlineColor: "primary",
      }}
    >
      <Show above="md">
        <Stack
          gap={5}
          w="200px"
          display={{ base: "none", md: "flex" }}
          align={"center"}
        >
          <Box w={"full"} aspectRatio={"3/2"} flexShrink={0}>
            {logo_image_url ? (
              <Image
                src={logo_image_url}
                alt={`${name}のロゴ`}
                aspectRatio={1 / 1}
                h="full"
                mx="auto"
              />
            ) : (
              <Flex
                bgColor={"brand.100"}
                align={"center"}
                justify={"center"}
                borderRadius={"4px"}
                p={3}
                aspectRatio={"3/2"}
              >
                <Text fontSize={"sm"} color={"textGray"}>
                  {name}
                </Text>
              </Flex>
            )}
          </Box>
          <BusinessEntityUseCaseTags tagsWidth="auto" useCase={useCase} />
        </Stack>
      </Show>
      <Stack gap={5} flex={1}>
        <Show below="md">
          <HeaderForSPSize />
        </Show>
        <Show above="md">
          <Stack gap={1}>
            <Box>
              <Text
                fontSize="lg"
                pl={0.5}
                display="inline"
                verticalAlign="middle"
              >
                {name}
              </Text>
              {branch_name && (
                <Text
                  as="span"
                  fontSize="sm"
                  color="textGray"
                  ml={1}
                  display="inline"
                  verticalAlign="middle"
                >
                  ({branch_name})
                </Text>
              )}
            </Box>
            <HStack gap={3}>
              <HStack gap={1}>
                <ArchitectureIcon boxSize={5} color={"brand.500"} />
                <Text fontSize={"sm"} pt={"1px"} color={"textGray"} mt={"1px"}>
                  {industry.name}
                </Text>
                <Text
                  fontSize={"xs"}
                  pt={"1px"}
                  color={"textGray"}
                  px={1}
                  borderRadius={"2px"}
                  bgColor={"gray.light"}
                >
                  {business_type}
                </Text>
              </HStack>
              <HStack gap={0.5}>
                <LocationOnIcon boxSize={5} color={"brand.500"} />
                <Text fontSize={"sm"} pt={"1px"} color={"textGray"} mt={"1px"}>
                  {prefecture.name}
                </Text>
              </HStack>
            </HStack>
          </Stack>
        </Show>
        <Stack
          pl={{ base: 0, md: 1 }}
          gap={5}
          py={5}
          borderTopColor={"brand.500"}
          borderBottomColor={"brand.500"}
          borderTopWidth={1}
          borderBottomWidth={1}
        >
          {(useCase === "both" || useCase === "contractor") &&
            contractor_info != null && (
              <Stack gap={4} pl={0.5}>
                <Stack
                  gap={{ base: 1, md: 4 }}
                  flexDir={{ base: "column", md: "row" }}
                  align={"flex-start"}
                >
                  <Stack
                    gap={4}
                    align={"center"}
                    flexDir={"row"}
                    flexShrink={0}
                  >
                    <Text
                      color={"brand.500"}
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight={"bold"}
                    >
                      受注したい業種
                    </Text>
                    <Divider
                      orientation="vertical"
                      h={4}
                      borderColor="brand.300"
                      display={{ base: "none", md: "flex" }}
                    />
                  </Stack>
                  <Flex columnGap={2} rowGap={1} flexWrap={"wrap"}>
                    {contractor_info.desired_industries.map(
                      (industry, index) => (
                        <Text
                          fontSize={"sm"}
                          key={`receivable-business-type-${index}`}
                        >
                          {industry.name}
                          {index < contractor_info.desired_industries.length - 1
                            ? ","
                            : ""}
                        </Text>
                      ),
                    )}
                  </Flex>
                </Stack>
                <Stack
                  gap={{ base: 1, md: 4 }}
                  flexDir={{ base: "column", md: "row" }}
                  align={"flex-start"}
                >
                  <Stack
                    gap={4}
                    align={"center"}
                    flexDir={"row"}
                    flexShrink={0}
                  >
                    <Text
                      color={"brand.500"}
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight={"bold"}
                    >
                      対応できる分野
                    </Text>
                    <Divider
                      orientation="vertical"
                      h={4}
                      borderColor="brand.300"
                      display={{ base: "none", md: "flex" }}
                    />
                  </Stack>
                  <Flex columnGap={2} rowGap={1} flexWrap={"wrap"}>
                    {contractor_info.supported_business_fields.map(
                      (field, index) => (
                        <Text
                          fontSize={"sm"}
                          key={`receivable-business-field-${index}`}
                        >
                          {field.name}
                          {index <
                          contractor_info.supported_business_fields.length - 1
                            ? ","
                            : ""}
                        </Text>
                      ),
                    )}
                  </Flex>
                </Stack>
              </Stack>
            )}
          {useCase === "both" &&
            client_info != null &&
            contractor_info != null && <BusinessEntityDottedLine />}
          {(useCase === "both" || useCase === "client") &&
            client_info != null && (
              <Stack gap={3} pl={0.5}>
                <Stack
                  gap={{ base: 1, md: 4 }}
                  flexDir={{ base: "column", md: "row" }}
                  align={"flex-start"}
                >
                  <Stack
                    gap={4}
                    align={"center"}
                    flexDir={"row"}
                    flexShrink={0}
                  >
                    <Text
                      color={"brand.500"}
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight={"bold"}
                    >
                      発注したい業務
                    </Text>
                    <Divider
                      orientation="vertical"
                      h={4}
                      borderColor="brand.300"
                      display={{ base: "none", md: "flex" }}
                    />
                  </Stack>
                  <Flex columnGap={2} rowGap={1} flexWrap={"wrap"}>
                    {client_info.desired_industries.map((type, index) => (
                      <Text
                        fontSize={"sm"}
                        key={`orderable-business-type-${index}`}
                      >
                        {type.name}
                        {index < client_info.desired_industries.length - 1
                          ? ","
                          : ""}
                      </Text>
                    ))}
                  </Flex>
                </Stack>
                <Stack
                  gap={{ base: 1, md: 4 }}
                  flexDir={{ base: "column", md: "row" }}
                  align={"flex-start"}
                >
                  <Stack
                    gap={4}
                    align={"center"}
                    flexDir={"row"}
                    flexShrink={0}
                  >
                    <Text
                      color={"brand.500"}
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight={"bold"}
                    >
                      発注したい分野
                    </Text>
                    <Divider
                      orientation="vertical"
                      h={4}
                      borderColor="brand.300"
                      display={{ base: "none", md: "flex" }}
                    />
                  </Stack>
                  <Flex columnGap={2} rowGap={1} flexWrap={"wrap"}>
                    {client_info.desired_business_fields.map((field, index) => (
                      <Text
                        fontSize={"sm"}
                        key={`orderable-business-field-${index}`}
                      >
                        {field.name}
                        {index < client_info.desired_business_fields.length - 1
                          ? ","
                          : ""}
                      </Text>
                    ))}
                  </Flex>
                </Stack>
              </Stack>
            )}
        </Stack>
        <Stack>
          <Text fontSize={"sm"} pl={0.5} noOfLines={{ base: 3, md: 2 }}>
            {introduction}
          </Text>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default BusinessEntityListItem;
